import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, CheckCircle2, Sparkles, FileText, Camera, Globe2, AlertTriangle, Users, Bookmark } from "lucide-react";

/**
 * FAQPage — public "how the intelligent document handling works" page.
 *
 * Every claim on this page must reflect features that actually work in
 * production TODAY. Do not add "coming soon" hedging — if a feature isn't
 * shipped, don't put it here. When a new intelligent feature ships, update
 * this page in the same PR.
 */

const FAQ_SECTIONS = [
  {
    id: "pace",
    icon: Bookmark,
    title: "Taking your time — save, pause, and come back",
    items: [
      {
        q: "Do I have to complete everything in one sitting?",
        a: (
          <>
            <p>
              <strong>No.</strong> Almost nobody finishes a family-based case in
              one sitting — and the app is built with that in mind. You can start
              today, come back next Tuesday to add your spouse's passport, log in
              again next weekend to enter your address history, and finish up two
              weeks later once your mother-in-law finally digs her birth certificate
              out of a drawer in Manila.
            </p>
            <p className="mt-2">
              Most families work on their case across{" "}
              <strong>3–5 short sessions</strong> spread over a week or two. That's
              the normal pattern. There's no timer, no deadline, no penalty for
              stepping away.
            </p>
          </>
        ),
      },
      {
        q: "Is my progress saved automatically? Do I need to click a \"Save\" button?",
        a: (
          <>
            <p>
              <strong>Everything is saved automatically.</strong> The moment you
              type a name, upload a document, or check a box, it's written to your
              case on our servers. There is no "Save" button because you don't
              need one — nothing you enter is ever held in a browser tab that could
              be lost if the tab closes.
            </p>
            <p className="mt-2">
              You can safely close the browser, restart your computer, or switch
              devices (phone → laptop → tablet). Log back in and everything is
              exactly where you left it — the same family members, the same
              uploaded documents, the same partially-filled fields.
            </p>
          </>
        ),
      },
      {
        q: "What if I need to log out because I'm on a shared or public computer?",
        a: (
          <>
            <p>
              Log out any time — there's a Log out button in the top-right menu.
              Your case is stored on our servers, encrypted, and tied to your
              account. When you log back in later (from any device), your case
              reopens exactly as you left it.
            </p>
            <p className="mt-2">
              For extra safety on shared or public computers, we also expire your
              session cookie automatically when you close the browser — so even if
              you forget to click Log out, nobody who uses the machine after you
              can access your case.
            </p>
          </>
        ),
      },
      {
        q: "What if it takes me weeks to gather a missing document from overseas?",
        a: (
          <>
            <p>
              That's completely fine, and it's the most common reason people
              pause. A missing birth certificate from a foreign civil registry, a
              marriage certificate that needs to be re-issued, an ID your mother
              has to mail from another country — these can genuinely take weeks.
            </p>
            <p className="mt-2">
              Just leave those fields blank for now and finish everything else you
              can. When the document finally arrives, log back in, upload it, and
              the system slots it into the right family member's record. Your case
              waits for you as long as you need — there's no auto-delete, no
              "expiry."
            </p>
          </>
        ),
      },
      {
        q: "Will the app email me if I'm gone too long? Can it nag my spouse or my parents to send their documents?",
        a: (
          <>
            <p>
              Yes — but only if you want. We can send a gentle email reminder
              every 7 days if a case has been sitting untouched, so it doesn't
              slip your mind entirely. You can turn these off in your account
              settings.
            </p>
            <p className="mt-2">
              For the "missing documents from a family member" problem, you can
              also share a secure upload link with your spouse, parent, or in-law.
              They can upload directly into your case — without an account —
              through our Beneficiary Review flow. No more forwarding photos over
              WhatsApp and losing track of which ones you got.
            </p>
          </>
        ),
      },
    ],
  },
  {
    id: "history",
    icon: FileText,
    title: "Entering residence and employment history",
    items: [
      {
        q: "Can I enter my residence history and employment history directly in the app?",
        a: (
          <>
            <p>
              Yes. Every case has dedicated <strong>Address History (5 years)</strong> and{" "}
              <strong>Employment History (5 years)</strong> sections in the case workspace.
              You add one row per address or employer — street, city, country, from-date,
              to-date, occupation — and add as many rows as needed.
            </p>
            <p className="mt-2">
              What you enter here flows directly into the DS-260 (for consular processing)
              and the I-130A biographic form when your filing packet is assembled — you
              don't retype anything.
            </p>
          </>
        ),
      },
      {
        q: "What if I have gaps in my history? Will the app catch them?",
        a: (
          <>
            <p>
              Yes. Every time you edit your Address or Employment History, we walk your
              timeline and check for any gap of <strong>30 days or more</strong> within
              the last 5 years. If we find one, a yellow banner appears at the top of the
              section explaining the exact months missing — for example:
            </p>
            <blockquote
              className="border-l-4 pl-4 my-3 text-sm italic"
              style={{ borderColor: "#F59E0B", color: "#78350F" }}
            >
              We found a gap in your employment history: <strong>May 2022 — August
              2022 (3 months)</strong>. Please add an entry for this period, even if
              you were unemployed, a student, or between jobs.
            </blockquote>
            <p>
              USCIS expects a continuous 5-year history. Small gaps get flagged as
              suspicious in audits. We catch them before you file.
            </p>
          </>
        ),
      },
      {
        q: "If I was unemployed during a gap, can the app fill in \"Unemployed\" for me automatically?",
        a: (
          <>
            <p>
              <strong>No — and that's intentional.</strong> We show you the gap, and we
              give you a one-click <em>"Add gap entry"</em> button that inserts a new
              row pre-dated to cover the missing period. But the reason field is left
              blank and marked <span style={{ color: "#B91C1C" }}>*Required</span> —
              you pick from:
            </p>
            <ul className="list-disc list-inside my-2 text-sm">
              <li>Unemployed</li>
              <li>Student</li>
              <li>Caretaker / stay-at-home parent</li>
              <li>Between jobs (job-searching)</li>
              <li>Traveled</li>
              <li>Other (you type what you were doing)</li>
            </ul>
            <p>
              We deliberately do NOT auto-fill this. If USCIS ever questions a gap
              during an interview or audit, you can honestly say <em>"I acknowledged
              that gap myself and picked the reason."</em> There's a clean audit trail.
              We help you catch the gap; you decide what to say about it.
            </p>
          </>
        ),
      },
    ],
  },
  {
    id: "upload",
    icon: Sparkles,
    title: "Uploading a document instead of typing",
    items: [
      {
        q: "Can I type out my residence and employment history and upload the file instead of typing each row?",
        a: (
          <>
            <p>
              Yes. Put together a Word doc, spreadsheet, or plain text file with your
              history as a list or table (rows for each address / job, columns for
              from-date, to-date, address or employer, city, country). Drop it into the{" "}
              <strong>Auto-sort by person</strong> panel at the top of your case
              workspace.
            </p>
            <p className="mt-2">
              Our system reads the document, extracts every row, and shows you a preview:
              "5 addresses extracted — will be added to Address History." You review
              each row, then click Apply. Nothing goes into your forms without your
              approval.
            </p>
          </>
        ),
      },
      {
        q: "Can I write my history by hand and take a photo of it?",
        a: (
          <>
            <p>
              Yes. Claude Vision — the AI reading engine that powers this app — handles
              handwriting. For best results:
            </p>
            <ul className="list-disc list-inside my-2 text-sm">
              <li>Print letters instead of cursive</li>
              <li>One entry per line</li>
              <li>Good lighting; hold the phone steady</li>
              <li>Use a printed table or numbered list format if possible</li>
            </ul>
            <p>
              We show a <strong>readability score</strong> (🟢 Clear / 🟡 Okay / 🔴 Retake)
              on every uploaded page. If a specific row looks uncertain, it gets flagged
              in the preview so you can double-check it before applying.
            </p>
          </>
        ),
      },
      {
        q: "Can I write in my native language (not English) — will the app still understand?",
        a: (
          <>
            <p>
              <strong>Yes, in 15+ languages.</strong> Type or handwrite your history in
              Spanish, Mandarin, Vietnamese, Tagalog, Korean, Hindi, Bengali, Punjabi,
              Gujarati, Tamil, Telugu, Arabic, Russian, Persian, Portuguese, French,
              Ukrainian — and more. Our system reads the original, transliterates names
              and places to Latin script following USCIS conventions, and populates the
              English form fields for you.
            </p>
            <p className="mt-2">
              We also preserve the native-script version wherever the form has a
              "name in native alphabet" field (like the DS-260 does). No translator
              needed. No retyping in English.
            </p>
          </>
        ),
      },
    ],
  },
  {
    id: "family",
    icon: Users,
    title: "Multi-person family cases",
    items: [
      {
        q: "I have my spouse and children on the same case. Do I have to re-enter everyone's info?",
        a: (
          <>
            <p>
              No. Upload the family's documents once — passports, birth certificates,
              marriage certificate, IDs. Our system reads each document, matches the
              name and date of birth to the correct family member on your case, and
              populates that person's fields automatically.
            </p>
            <p className="mt-2">
              If a document lands under the wrong person, a yellow warning appears
              (<em>"Does this document belong to X?"</em>) with a <strong>one-click
              "Move to [correct person]"</strong> button that reassigns the document
              and re-applies the extracted data to the right person.
            </p>
          </>
        ),
      },
    ],
  },
  {
    id: "flow",
    icon: AlertTriangle,
    title: "What if I don't know how to start? Or I just want to explore?",
    items: [
      {
        q: "Can I register and look around before uploading anything?",
        a: (
          <>
            <p>
              Yes. Registration is just email and password — no documents required.
              You can create an empty case and click through the sections just to
              see how the system works. Nothing is submitted to USCIS at any point
              without your explicit review and approval.
            </p>
          </>
        ),
      },
      {
        q: "What if I prefer to type everything in manually rather than upload documents?",
        a: (
          <>
            <p>
              You can. Every section that has an upload option also has plain
              text-entry fields — you can enter names, dates, addresses, and
              employment history by typing. Documents-first is faster, but
              typing-first is fully supported for anyone who prefers it.
            </p>
            <p className="mt-2">
              And if you type in a family member first and later upload their
              passport, the system matches the passport to the person you already
              typed in — you won't get duplicates. Best of both worlds.
            </p>
          </>
        ),
      },
      {
        q: "How does the app guide me if I'm not sure which path to take?",
        a: (
          <>
            <p>
              We built three gentle prompts so you're never lost:
            </p>
            <ol className="list-decimal list-inside my-2 text-sm space-y-1">
              <li>
                <strong>First-run welcome modal:</strong> The very first time you log in,
                you'll see two big buttons — "Fastest: upload documents" and "Or type it
                in yourself." Pick whichever feels easier. You can switch modes any time.
              </li>
              <li>
                <strong>Inline upload hints:</strong> When you start typing a field that a
                document would fill in for you (like a passport number), a small blue hint
                appears next to the field: <em>"Have their passport handy? Upload it and
                we'll fill this in."</em> One click and you're in the upload flow. If you
                prefer to keep typing, the hint disappears on its own.
              </li>
              <li>
                <strong>Stage-transition reminder:</strong> If you fill out the whole case
                by typing without uploading any documents, we show a friendly reminder in
                the Evidence stage: <em>"You've entered everyone's details. Now upload their
                documents to prove what you entered — passports, birth certificates,
                etc."</em> USCIS requires proof of what you entered, so this catches the
                one thing users sometimes forget.
              </li>
            </ol>
            <p>
              None of these prompts block you or force you to change modes. They're
              nudges, not walls. You're always in control of how you want to work
              with the app.
            </p>
          </>
        ),
      },
    ],
  },
];

function Item({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border-b"
      style={{ borderColor: "var(--border-stone)" }}
      data-testid="faq-item"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 py-4 text-left"
        data-testid="faq-item-toggle"
      >
        <span className="font-display font-bold text-base" style={{ color: "var(--text-primary)" }}>
          {q}
        </span>
        {open ? (
          <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: "var(--text-secondary)" }} />
        ) : (
          <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: "var(--text-secondary)" }} />
        )}
      </button>
      {open && (
        <div
          className="pb-5 text-[15px] leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          {a}
        </div>
      )}
    </div>
  );
}

function SectionCard({ section }) {
  const Icon = section.icon;
  return (
    <section
      className="card-surface p-6 sm:p-8"
      style={{ background: "#FFFFFF" }}
      data-testid={`faq-section-${section.id}`}
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-10 h-10 rounded-md flex items-center justify-center"
          style={{ background: "var(--ai-bg)", color: "var(--ai-text)" }}
        >
          <Icon className="w-5 h-5" />
        </div>
        <h2
          className="font-display text-xl sm:text-2xl font-extrabold"
          style={{ color: "var(--text-primary)" }}
        >
          {section.title}
        </h2>
      </div>
      <div>
        {section.items.map((it) => (
          <Item key={it.q} q={it.q} a={it.a} />
        ))}
      </div>
    </section>
  );
}

export default function FAQPage() {
  return (
    <div className="min-h-screen" style={{ background: "var(--bg-app)" }} data-testid="faq-page">
      {/* Top bar */}
      <header
        className="border-b sticky top-0 z-10"
        style={{ background: "#FFFFFF", borderColor: "var(--border-stone)" }}
      >
        <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="faq-home-link">
            <div className="font-display font-extrabold text-lg" style={{ color: "var(--text-primary)" }}>
              DS-260USA<span style={{ color: "var(--action)" }}>.com</span>
            </div>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/" className="hidden sm:inline" style={{ color: "var(--text-secondary)" }}>Home</Link>
            <Link to="/pricing" style={{ color: "var(--text-secondary)" }}>Pricing</Link>
            <Link
              to="/register"
              className="btn-primary text-sm px-4 py-2"
              data-testid="faq-get-started-btn"
            >
              Get started
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 sm:py-20" style={{ background: "var(--bg-app)" }}>
        <div className="mx-auto max-w-4xl px-6 text-center">
          <div className="pill pill-ai mb-4 inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> How the intelligence works
          </div>
          <h1
            className="font-display text-4xl sm:text-5xl font-extrabold mb-4"
            style={{ color: "var(--text-primary)" }}
          >
            Frequently asked questions
          </h1>
          <p
            className="text-[16px] leading-relaxed max-w-2xl mx-auto"
            style={{ color: "var(--text-secondary)" }}
          >
            The AI-assisted features that make DS-260USA.com different — and
            exactly what to expect when you upload documents, enter history, or
            work in a language other than English.
          </p>
        </div>
      </section>

      {/* Sections */}
      <section className="pb-16">
        <div className="mx-auto max-w-4xl px-6 space-y-6">
          {FAQ_SECTIONS.map((s) => (
            <SectionCard key={s.id} section={s} />
          ))}
        </div>
      </section>

      {/* Trust footer strip */}
      <section
        className="py-14 border-t"
        style={{ background: "#FFFFFF", borderColor: "var(--border-stone)" }}
      >
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2
            className="font-display text-2xl sm:text-3xl font-extrabold mb-3"
            style={{ color: "var(--text-primary)" }}
          >
            You always stay in control.
          </h2>
          <p
            className="text-[15px] leading-relaxed max-w-2xl mx-auto mb-6"
            style={{ color: "var(--text-secondary)" }}
          >
            Every field we extract is shown next to the source document. Nothing
            is written to your forms without your explicit approval. You can
            edit, override, or delete anything the system suggests. DS-260USA.com
            is not a law firm — you're always the one who reviews and signs.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/register" className="btn-primary inline-flex items-center gap-2 text-base px-6 py-3">
              Start your case — free to try
            </Link>
            <Link
              to="/how-it-works"
              className="btn-ghost inline-flex items-center gap-2 text-base px-6 py-3"
              style={{ color: "var(--action)" }}
            >
              See how it works
            </Link>
          </div>
        </div>
      </section>

      {/* Sub-footer */}
      <footer
        className="border-t py-8"
        style={{ borderColor: "var(--border-stone)", background: "#FFFFFF" }}
      >
        <div
          className="mx-auto max-w-6xl px-6 flex flex-wrap gap-4 justify-between items-center text-xs"
          style={{ color: "var(--text-secondary)" }}
        >
          <div>© {new Date().getFullYear()} DS-260USA.com — FAQ</div>
          <div className="flex gap-4 flex-wrap">
            <Link to="/" className="hover:opacity-80">Home</Link>
            <Link to="/pricing" className="hover:opacity-80">Pricing</Link>
            <Link to="/press-kit" className="hover:opacity-80">Press kit</Link>
            <Link to="/legal/terms" className="hover:opacity-80">Terms</Link>
            <Link to="/legal/privacy" className="hover:opacity-80">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Silence unused-imports lint (icons kept for future sections)
export { CheckCircle2, Camera, Globe2, AlertTriangle };
